import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getIdeas } from 'api/companyMapping';
import { getConversation } from 'api/conversations';
import { PresentationContainer } from 'components/CompanyMapping/Presentation';
import useSlideNavigation from 'hooks/useSlideNavigation';

export default function CompanyMappingPresentation() {
    const { conversationId } = useParams();

    // Fetch conversation for mapping name
    const { data: conversation } = useQuery({
        queryKey: ['conversations', conversationId],
        queryFn: () => getConversation({ id: conversationId }),
        enabled: !!conversationId
    });

    // Fetch ideas
    const { data: ideas = [], isLoading } = useQuery({
        queryKey: ['company-mapping-ideas', conversationId],
        queryFn: () => getIdeas({ conversationId }),
        enabled: !!conversationId
    });

    // Group ideas by category
    const ideasByCategory = useMemo(() => {
        const grouped = ideas.reduce((acc, idea) => {
            const category = idea.category || 'uncategorized';
            const cageoryIndex = acc.findIndex(cat => cat.value === category);
            if (cageoryIndex === -1) {
                acc = [
                    ...acc,
                    {
                        _id: acc.length,
                        value: category,
                        ideas: [idea]
                    }
                ];
            } else {
                acc[cageoryIndex].ideas.push(idea);
            }
            return acc;
        }, []);
        return grouped;
    }, [ideas]);

    console.log(ideasByCategory);

    // Calculate quick wins (ease >= 7 && impact >= 7)
    const quickWins = useMemo(() => {
        return ideas.filter(idea => {
            const ease = idea.ease || (10 - idea.difficulty + 1);
            return ease >= 7 && idea.impact >= 7;
        }).sort((a, b) => {
            const aScore = a.impact + (a.ease || (10 - a.difficulty + 1));
            const bScore = b.impact + (b.ease || (10 - b.difficulty + 1));
            return bScore - aScore;
        });
    }, [ideas]);

    // Build slides array dynamically
    const slides = useMemo(() => {
        const slideArray = [];
        let sectionNum = 0;

        // 1. Cover (always)
        slideArray.push({
            type: 'cover',
            data: { totalIdeas: ideas.length }
        });

        if (ideas.length > 0) {
            // 2. Overview
            sectionNum++;
            slideArray.push({
                type: 'overview',
                sectionNumber: String(sectionNum).padStart(2, '0')
            });

            // 3. Category slides + individual idea slides
            ideasByCategory.forEach(categoryData => {
                if (categoryData.ideas && categoryData.ideas.length > 0) {
                    sectionNum++;
                    const currentSectionNum = String(sectionNum).padStart(2, '0');

                    // Category overview slide
                    slideArray.push({
                        type: 'category',
                        sectionNumber: currentSectionNum,
                        data: {
                            category: categoryData.value,
                            ideas: categoryData.ideas
                        }
                    });

                    // Individual idea slides for this category
                    categoryData.ideas.forEach((idea, ideaIndex) => {
                        slideArray.push({
                            type: 'idea',
                            sectionNumber: currentSectionNum,
                            data: {
                                idea,
                                ideaIndex,
                                totalIdeasInCategory: categoryData.ideas.length
                            }
                        });
                    });
                }
            });

            // 4. Matrix
            sectionNum++;
            slideArray.push({
                type: 'matrix',
                sectionNumber: String(sectionNum).padStart(2, '0')
            });

            // 5. Quick Wins (if any)
            if (quickWins.length > 0) {
                sectionNum++;
                slideArray.push({
                    type: 'quickWins',
                    sectionNumber: String(sectionNum).padStart(2, '0')
                });
            }

            // 6. Summary
            slideArray.push({ type: 'summary' });
        }

        return slideArray;
    }, [ideas, ideasByCategory, quickWins]);

    // Navigation hook
    const navigation = useSlideNavigation({
        totalSlides: slides.length,
        exitPath: `/tools/company-mapping/${conversationId}`
    });

    const mappingName = conversation?.name || 'Mappatura Aziendale';

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
                <div className="text-white text-xl">Caricamento...</div>
            </div>
        );
    }

    return (
        <PresentationContainer
            slides={slides}
            currentSlide={navigation.currentSlide}
            onPrev={navigation.goPrev}
            onNext={navigation.goNext}
            onGoTo={navigation.goTo}
            onExit={navigation.exit}
            isFirst={navigation.isFirst}
            isLast={navigation.isLast}
            totalSlides={navigation.totalSlides}
            ideas={ideas}
            ideasByCategory={ideasByCategory}
            quickWins={quickWins}
            mappingName={mappingName}
        />
    );
}
